import { FC, useEffect, useState } from 'react';
import { EditPlayer, EditPlayerTableProps } from './sweepReport.type';
import {
  Player,
  PlayerUpdate,
  POIClassification,
  SweepStatus,
  SweepType,
} from '@inplay/models';
import { Trans } from 'react-i18next';
import AddNewPlayerRow from '../../component/addNewPlayerModal/addPlayerRow/addNewPlayerRow';
import {
  renderToggleButton,
  renderConsecutiveSweep,
  renderConsecutiveSweepFlag,
} from '../../component/addNewPlayerModal/addNewPlayerHelper';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';

const EditPlayerTable: FC<EditPlayerTableProps> = (props) => {
  const isConsecutiveEditable =
    props.selectedSweep?.status === SweepStatus.Completed &&
    props.selectedSweep?.type === SweepType.Obligatory;
  const isEditable = props.selectedSweep?.status === SweepStatus.Missed;
  const { isTablet } = useResponsiveLayout();
  const [players, setPlayers] = useState<EditPlayer[]>(
    props.selectedSweep?.players ??
      [].map((player: Player) => ({
        ...player,
        presentDuringSweep: player.consecutiveSweepsCount > 0,
      })) ??
      [],
  );

  const handlePlayerUpdate = (updatedPlayer: EditPlayer) => {
    props.setUpdatedPlayers((previousPlayer) => {
      const playerExist: EditPlayer | undefined = previousPlayer.find(
        (player) => player.id === updatedPlayer.id,
      );
      if (playerExist) {
        return previousPlayer.map((player: Player) =>
          player.id === updatedPlayer.id ? updatedPlayer : player,
        );
      }
      return [...previousPlayer, updatedPlayer];
    });
  };

  const togglePresentSweep = (id: string, value: boolean) => {
    if (isEditable) return;
    setPlayers((previousPlayers) =>
      previousPlayers.map((player: EditPlayer) => {
        if (player.id !== id) return player;
        const updatedPlayer = {
          ...player,
          ...(value && !player.presentDuringSweep
            ? {
                // from No -> Yes
                presentDuringSweep: true,
                consecutiveSweepsCount:
                  player.previousConsecutiveSweepsCount ?? 1,
              }
            : !value && player.presentDuringSweep
              ? {
                  // from Yes -> No
                  presentDuringSweep: false,
                  previousConsecutiveSweepsCount: player.consecutiveSweepsCount,
                  consecutiveSweepsCount: 0,
                }
              : {}),
        };
        handlePlayerUpdate(updatedPlayer);
        return updatedPlayer;
      }),
    );
  };

  const toggleGamblingHarm = (id: string, value: boolean) => {
    if (isEditable) return;
    setPlayers((previousPlayers) =>
      previousPlayers.map((player: EditPlayer) => {
        if (player.id !== id) return player;
        const updatedPlayer = {
          ...player,
          poiClassification: value
            ? POIClassification.POI
            : POIClassification.NON_POI,
        };
        handlePlayerUpdate(updatedPlayer);
        return updatedPlayer;
      }),
    );
  };

  useEffect(() => {
    if (!props.selectedSweep?.players) return;

    const originalPlayers: Player[] = props.selectedSweep.players;
    const combinedPlayers: PlayerUpdate[] = [...players, ...props.newPlayer];

    const updatedOrNewPlayer: PlayerUpdate[] = combinedPlayers.filter(
      (player: PlayerUpdate) => {
        const original = originalPlayers.find(
          (originalPlayer: PlayerUpdate) => originalPlayer.id === player.id,
        );
        return !original || JSON.stringify(player) !== JSON.stringify(original);
      },
    );
    const mergedPlayers: Map<string, PlayerUpdate> = new Map<
      string,
      PlayerUpdate
    >();

    originalPlayers.forEach((player: PlayerUpdate) => {
      mergedPlayers.set(player.id, player);
    });

    updatedOrNewPlayer.forEach((player: PlayerUpdate) => {
      mergedPlayers.set(player.id, player);
    });

    props.setUpdatedPlayers(Array.from(mergedPlayers.values()) as EditPlayer[]);
  }, [players, props.newPlayer, props.selectedSweep]);

  const setPlayerSummary = (updatedPlayer: PlayerUpdate): void => {
    setPlayers((prevPlayers) =>
      prevPlayers.map((player: EditPlayer) =>
        player.id === updatedPlayer.id
          ? { ...player, ...updatedPlayer }
          : player,
      ),
    );
  };

  return (
    <div className="mx-10 my-4">
      <table className="min-w-full text-sm text-left text-gray-600">
        <thead className="bg-[#FAFAFA] font-semibold text-gray-900">
          <tr>
            <th className="px-4 py-4 text-[#151740] font-medium">
              <Trans i18nKey="sweeps.table.id" />
            </th>
            {isTablet ? (
              <th className="px-4 py-4 text-[#151740] font-medium">
                <Trans i18nKey="sweeps.table.photo" /> &{' '}
                <Trans i18nKey="common.description" />
              </th>
            ) : (
              <>
                <th className="px-4 py-4 text-[#151740] font-medium">
                  <Trans i18nKey="sweeps.table.photo" />
                </th>
                <th className="px-4 py-4 text-[#151740] font-medium">
                  <Trans i18nKey="common.description" />
                </th>
              </>
            )}

            <th className="px-4 py-4 text-[#151740] font-medium">
              <Trans i18nKey="sweeps.table.presentDuringSweep" />
            </th>
            <th className="px-4 py-4 text-[#151740] font-medium">
              <Trans i18nKey="sweeps.table.signOfGamblingHarm" />
            </th>
            <th className="px-4 py-4 text-[#151740] font-medium">
              <Trans i18nKey="sweeps.table.consecutiveSweep" />
            </th>
          </tr>
        </thead>
        <tbody>
          {players.map((player: Player) => (
            <>
              <tr key={player.id} className="border-b">
                <td className="px-4 py-4 text-[#151740] flex flex-col">
                  {player.id.length > 5 ? player.id.substring(0, 5) : player.id}
                  {isTablet &&
                    renderConsecutiveSweepFlag(player, setPlayerSummary)}
                </td>

                {isTablet ? (
                  <td className="px-4 py-2 text-[#151740] whitespace-normal break-words max-w-[300px]">
                    <div className=" flex flex-col">
                      <span>{player.description}</span>
                      {player.bodyImage ? (
                        <img
                          src={player.bodyImage}
                          alt="avatar"
                          className="w-10 h-10 object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-200 flex items-center justify-center text-xs text-gray-500">
                          <Trans i18nKey="sweeps.table.noImage" />
                        </div>
                      )}
                    </div>
                  </td>
                ) : (
                  <>
                    <td className="px-4 py-4">
                      {player.bodyImage ? (
                        <img
                          src={player.bodyImage}
                          alt="avatar"
                          className="w-10 h-10 object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-200 flex items-center justify-center text-xs text-gray-500">
                          <Trans i18nKey="sweeps.table.noImage" />
                        </div>
                      )}
                    </td>

                    <td className="px-4 py-2 text-[#151740] whitespace-normal break-words max-w-[300px]">
                      {player.description}
                    </td>
                  </>
                )}

                <td className="px-4 py-2">
                  {renderToggleButton(
                    player.presentDuringSweep,
                    null,
                    'bg-[#C6FF22] text-[#151740] hover:bg-[#C6FF22] hover:text-[#151740]',
                    'bg-[#151740] text-white border-gray-300 hover:bg-[#151740]',
                    isEditable,
                    (newValue: boolean) =>
                      togglePresentSweep(player.id, newValue),
                  )}
                </td>

                <td className="px-4 py-2">
                  {renderToggleButton(
                    player.poiClassification === POIClassification.POI,
                    null,
                    'bg-[#D30002] text-white hover:bg-[#D30002]',
                    'bg-[#151740] text-white border-gray-300 hover:bg-[#151740]',
                    isEditable,
                    (newValue: boolean) =>
                      toggleGamblingHarm(player.id, newValue),
                  )}
                </td>

                <td className="px-4 py-2 text-start">
                  {renderConsecutiveSweep(
                    player,
                    setPlayerSummary,
                    !isConsecutiveEditable,
                    isTablet,
                  )}
                </td>
              </tr>
            </>
          ))}

          <AddNewPlayerRow
            newPlayers={props.newPlayer}
            setNewPlayer={props.setNewPlayer}
            handleEditPlayer={props.handleEditPlayer}
            venueBranchOpeningTime={
              props.selectedSweep?.venueBranchOpeningTime!
            }
            sweepTime={props.selectedSweep?.createdOn!}
            venueBranchClosingTime={
              props.selectedSweep?.venueBranchClosingTime!
            }
          />
        </tbody>
      </table>
    </div>
  );
};

export default EditPlayerTable;
